/**
 * Health Check Quiz Session Storage
 * Manages localStorage for quiz state persistence across page refreshes
 * Sessions are stored permanently until explicitly cleared
 */

const STORAGE_PREFIX = "healthcheck_session_";

/**
 * Generate storage key for a user's quiz session
 */
const getStorageKey = (userId, bookTypeId) => {
  return `${STORAGE_PREFIX}${userId}_${bookTypeId}`;
};

/**
 * Save quiz session to localStorage
 * @param {string} userId - User ID
 * @param {string} bookTypeId - Book type ID
 * @param {object} sessionData - Quiz session data
 */
export const saveHealthCheckSession = (userId, bookTypeId, sessionData) => {
  try {
    const key = getStorageKey(userId, bookTypeId);
    const data = {
      ...sessionData,
      timestamp: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    // Error saving session
  }
};

/**
 * Load quiz session from localStorage
 * @param {string} userId - User ID
 * @param {string} bookTypeId - Book type ID
 * @returns {object|null} - Session data or null if not found
 */
export const loadHealthCheckSession = (userId, bookTypeId) => {
  try {
    const key = getStorageKey(userId, bookTypeId);
    const stored = localStorage.getItem(key);

    if (!stored) {
      return null;
    }

    const data = JSON.parse(stored);
    return data;
  } catch (error) {
    return null;
  }
};

/**
 * Clear quiz session from localStorage
 * @param {string} userId - User ID
 * @param {string} bookTypeId - Book type ID
 */
export const clearHealthCheckSession = (userId, bookTypeId) => {
  try {
    const key = getStorageKey(userId, bookTypeId);
    localStorage.removeItem(key);
  } catch (error) {
    // Error clearing session
  }
};

/**
 * Check if a quiz session exists
 * @param {string} userId - User ID
 * @param {string} bookTypeId - Book type ID
 * @returns {boolean}
 */
export const hasHealthCheckSession = (userId, bookTypeId) => {
  const session = loadHealthCheckSession(userId, bookTypeId);
  return session !== null;
};

/**
 * Get all active health check sessions for a user
 * @param {string} userId - User ID
 * @returns {Array} - Array of session data objects
 */
export const getAllUserSessions = (userId) => {
  try {
    const sessions = [];
    const prefix = `${STORAGE_PREFIX}${userId}_`;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        const stored = localStorage.getItem(key);
        if (stored) {
          const data = JSON.parse(stored);
          sessions.push({
            key,
            bookTypeId: key.replace(prefix, ""),
            ...data
          });
        }
      }
    }

    return sessions;
  } catch (error) {
    return [];
  }
};

/**
 * Clear all health check sessions for a user
 * @param {string} userId - User ID
 */
export const clearAllUserSessions = (userId) => {
  try {
    const prefix = `${STORAGE_PREFIX}${userId}_`;
    const keysToRemove = [];

    // Collect all keys to remove
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }

    // Remove all collected keys
    keysToRemove.forEach((key) => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    // Error clearing all sessions
  }
};
